// Currency utility functions for HR ERP system

/**
 * Format currency amount for display
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code (USD, PKR, etc.)
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency, locale = 'en-US') {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }
  
  const numAmount = Number.parseFloat(amount);
  if (isNaN(numAmount)) return '-';
  
  const currencySymbols = {
    USD: '$',
    PKR: '₨',
    EUR: '€',
    GBP: '£'
  };
  
  // `|| ''`: an old appraisal can carry an amount with no currency, which printed "null5,000".
  const symbol = currencySymbols[currency] || currency || '';
  
  return `${symbol}${numAmount.toLocaleString(locale)}`;
}

/**
 * Format an appraisal's raise for display. Every appraisal now records a raise, and 0 is
 * how HR records "no raise"; it arrives as the string '0.00', which is truthy, so a plain
 * `amount ? ... : fallback` check shows it as a formatted zero.
 * @param {number|string|null} amount - raise_amount as the API sends it
 * @param {string|null} currency - Currency code
 * @param {string} blank - Shown for an older appraisal saved without a raise
 * @returns {string}
 */
export function formatRaise(amount, currency, blank = '-') {
  if (amount === null || amount === undefined || amount === '') return blank;
  const numAmount = Number.parseFloat(amount);
  if (Number.isNaN(numAmount)) return '-';
  if (numAmount === 0) return 'No raise';
  // Cents always show two decimals ("$1,500.50"), whole amounts none ("$2,500"), the same
  // rule as the server's raise messages. A decrement puts its sign first ("-$2,500"), where
  // formatCurrency would print "$-2,500".
  const abs = Math.abs(numAmount);
  const text = formatCurrencyWithDecimals(abs, currency || '', Number.isInteger(abs) ? 0 : 2);
  return numAmount < 0 ? `-${text}` : text;
}

/**
 * Format currency amount with decimal places
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code (USD, PKR, etc.)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string with decimals
 */
export function formatCurrencyWithDecimals(amount, currency, decimals = 2, locale = 'en-US') {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }
  
  const numAmount = Number.parseFloat(amount);
  if (isNaN(numAmount)) return '-';
  
  const currencySymbols = {
    USD: '$',
    PKR: '₨',
    EUR: '€',
    GBP: '£'
  };
  
  const symbol = currencySymbols[currency] || currency;
  
  return `${symbol}${numAmount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
}

/**
 * Get currency symbol
 * @param {string} currency - Currency code (USD, PKR, etc.)
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency) {
  const currencySymbols = {
    USD: '$',
    PKR: '₨',
    EUR: '€',
    GBP: '£'
  };
  
  return currencySymbols[currency] || currency;
}

/**
 * Convert amount between currencies (basic conversion)
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @param {Object} rates - Exchange rates object
 * @returns {number} Converted amount
 */
export function convertCurrency(amount, fromCurrency, toCurrency, rates) {
  if (!rates || !rates[fromCurrency] || !rates[toCurrency]) {
    return amount; // Return original amount if rates not available
  }
  
  const fromRate = rates[fromCurrency];
  const toRate = rates[toCurrency];
  
  // Convert to base currency first, then to target currency
  const baseAmount = amount / fromRate;
  return baseAmount * toRate;
}

/**
 * Calculate percentage of total
 * @param {number} amount - Amount to calculate percentage for
 * @param {number} total - Total amount
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {number} Percentage value
 */
export function calculatePercentage(amount, total, decimals = 1) {
  if (total === 0) return 0;
  const percentage = (amount / total) * 100;
  return Number.parseFloat(percentage.toFixed(decimals));
}

/**
 * Format percentage for display
 * @param {number} percentage - Percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export function formatPercentage(percentage, decimals = 1) {
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Calculate raise amount based on percentage
 * @param {number} currentSalary - Current salary amount
 * @param {number} raisePercentage - Raise percentage
 * @returns {number} Raise amount
 */
export function calculateRaiseAmount(currentSalary, raisePercentage) {
  return (currentSalary * raisePercentage) / 100;
}

/**
 * Calculate new salary after raise
 * @param {number} currentSalary - Current salary amount
 * @param {number} raisePercentage - Raise percentage
 * @returns {number} New salary amount
 */
export function calculateNewSalary(currentSalary, raisePercentage) {
  const raiseAmount = calculateRaiseAmount(currentSalary, raisePercentage);
  return currentSalary + raiseAmount;
}

/**
 * Validate currency code
 * @param {string} currency - Currency code to validate
 * @returns {boolean} True if valid currency code
 */
export function isValidCurrency(currency) {
  const validCurrencies = ['USD', 'PKR', 'EUR', 'GBP'];
  return validCurrencies.includes(currency?.toUpperCase());
}

/**
 * Get supported currencies
 * @returns {Array} Array of supported currency codes
 */
export function getSupportedCurrencies() {
  return [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' }
  ];
} 